/**
 * What happened on a booked call (owner ask 2026-10-09): the outcome pill the
 * calendar shows on a callback whose time has passed, and the small select a
 * recruiter uses to correct it.
 *
 * The outcome is worked out by the server (services/crmCallbackOutcome.js, a
 * crm-worker sweep every few minutes) from the call rows, stage moves, ICF sends
 * and new callbacks around the slot; connected calls are read by the AI. A
 * person's choice (PUT /api/crm/callbacks/:id/outcome) always wins; "Automatic"
 * (DELETE) hands it back to the records.
 */
import { useState } from 'react';
import { Sparkles, UserCheck } from 'lucide-react';
import api from '../../../services/api';
import { CalendarItem, CallbackOutcome, CallbackOutcomeKey } from '../../types';
import { clockTime } from '../../lib';

/** Menu order and labels (backend MANUAL_OUTCOMES / OUTCOMES, KEEP IN STEP). */
export const OUTCOME_OPTIONS: { key: CallbackOutcomeKey; label: string }[] = [
  { key: 'talked', label: 'Talked' },
  { key: 'no_answer', label: 'No answer' },
  { key: 'voicemail', label: 'Left voicemail' },
  { key: 'interested', label: 'Interested' },
  { key: 'wants_call_back', label: 'Wants a call back' },
  { key: 'callback_booked', label: 'Call back booked' },
  { key: 'icf_sent', label: 'ICF sent' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'not_interested', label: 'Not interested' },
  { key: 'not_eligible', label: 'Not eligible' },
  { key: 'wrong_number', label: 'Wrong number' },
  { key: 'asked_to_stop', label: 'Asked to stop' },
  { key: 'not_called', label: 'Not called' },
];

type Tone = 'good' | 'info' | 'warn' | 'bad' | 'alert' | 'muted';

export const OUTCOME_TONE: Record<CallbackOutcomeKey, Tone> = {
  scheduled: 'good',
  icf_sent: 'good',
  interested: 'good',
  talked: 'good',
  callback_booked: 'info',
  wants_call_back: 'info',
  voicemail: 'warn',
  no_answer: 'warn',
  not_interested: 'bad',
  not_eligible: 'bad',
  wrong_number: 'bad',
  asked_to_stop: 'bad',
  not_called: 'alert',
  cancelled: 'muted',
};

const PILL: Record<Tone, string> = {
  good: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  info: 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300',
  warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  bad: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
  alert: 'border border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400',
  muted: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

const DOT: Record<Tone, string> = {
  good: 'bg-green-500',
  info: 'bg-primary-500',
  warn: 'bg-amber-500',
  bad: 'bg-rose-500',
  alert: 'bg-red-600',
  muted: 'bg-gray-400',
};

export const outcomeDotCls = (o: CallbackOutcome) => DOT[OUTCOME_TONE[o.key] || 'muted'];

/** A callback whose slot has passed (the only kind an outcome is shown for). */
export function isPastCallback(item: CalendarItem, now = Date.now()): boolean {
  return item.source_kind === 'callback' && new Date(item.start).getTime() <= now;
}

const shortDayTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} ${clockTime(iso)}`;
};

/** The words a pill title carries: how it was decided, plus the call counts. */
export function outcomeTitle(o: CallbackOutcome): string {
  const how = o.source === 'manual' ? 'Set by a person' : o.source === 'ai' ? 'From the AI reading the call' : 'Worked out from the records';
  const calls = o.calls && o.calls.tries + o.calls.connected > 0
    ? ` · ${o.calls.tries} ${o.calls.tries === 1 ? 'try' : 'tries'}, ${o.calls.connected} connected`
    : '';
  const closed = o.auto_closed ? ` · callback marked ${o.auto_closed === 'no_show' ? 'no-show' : 'completed'} automatically` : '';
  return `${o.label}: ${how}${calls}${closed}`;
}

export function OutcomePill({ outcome, compact = false }: { outcome: CallbackOutcome; compact?: boolean }) {
  const tone = OUTCOME_TONE[outcome.key] || 'muted';
  return (
    <span
      title={outcomeTitle(outcome)}
      data-outcome={outcome.key}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${PILL[tone]}`}
    >
      {outcome.source === 'ai' && <Sparkles className="h-3 w-3" aria-label="Read by the AI" />}
      {outcome.source === 'manual' && !compact && <UserCheck className="h-3 w-3" aria-label="Set by a person" />}
      {outcome.label}
    </span>
  );
}

/** The other things that happened, as quiet words after the pill ("ICF sent · Call back Thu 2:00 PM"). */
export function secondaryLine(o: CallbackOutcome): string {
  const rest = o.signals.slice(1).map((s) => {
    if (s.key === 'callback_booked' && o.next_callback_at) return `Call back ${shortDayTime(o.next_callback_at)}`;
    return s.label;
  });
  if (o.key === 'callback_booked' && o.next_callback_at) rest.unshift(shortDayTime(o.next_callback_at));
  return rest.join(' · ');
}

/**
 * Pill, the secondary signals and the one-line summary, for a list row.
 * `pending` is a past callback the server has not worked out yet.
 */
export function OutcomeBlock({ outcome }: { outcome: CallbackOutcome | null | undefined }) {
  if (!outcome) {
    return (
      <span className="inline-flex items-center whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400" title="The outcome is worked out a few minutes after the call">
        Checking…
      </span>
    );
  }
  const more = secondaryLine(outcome);
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
      <OutcomePill outcome={outcome} />
      {more && <span className="truncate text-xs text-gray-500 dark:text-gray-400">{more}</span>}
    </span>
  );
}

const errMsg = (e: any): string =>
  e?.response?.data?.error || e?.message || 'Could not save the outcome';

/**
 * The correction control: a compact native select. Picking an outcome saves it
 * as the person's choice; "Automatic" (shown once a person has chosen) hands it
 * back to the records.
 */
export function OutcomeSelect({
  item,
  onSaved,
  onError,
}: {
  item: CalendarItem;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const current = item.outcome;
  const value = current?.source === 'manual' ? current.key : '';
  const change = async (next: string) => {
    if (busy) return;
    setBusy(true);
    try {
      if (next === '__auto') await api.delete(`/api/crm/callbacks/${item.source_id}/outcome`);
      else if (next) await api.put(`/api/crm/callbacks/${item.source_id}/outcome`, { outcome: next });
      onSaved();
    } catch (e) {
      onError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <select
      aria-label={`Change the outcome of the call with ${item.contact_name || 'this lead'}`}
      title="Set the outcome by hand"
      value={value}
      disabled={busy}
      onChange={(e) => change(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className="min-h-[44px] w-[8.5rem] flex-shrink-0 rounded-lg max-sm:ml-auto border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-600 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 sm:min-h-0"
    >
      <option value="">Edit outcome</option>
      {current?.source === 'manual' && <option value="__auto">Automatic (from the records)</option>}
      {OUTCOME_OPTIONS.map((o) => (
        <option key={o.key} value={o.key}>{o.label}</option>
      ))}
    </select>
  );
}

/** The day header's tally of past callbacks: "3 talked · 2 no answer · 1 not called". */
export function outcomeTally(items: CalendarItem[]): { label: string; tone: Tone; n: number }[] {
  const counts = new Map<CallbackOutcomeKey, number>();
  for (const it of items) {
    if (it.source_kind !== 'callback' || !it.outcome) continue;
    counts.set(it.outcome.key, (counts.get(it.outcome.key) || 0) + 1);
  }
  const order = OUTCOME_OPTIONS.map((o) => o.key).concat(['cancelled']);
  return order
    .filter((k) => counts.has(k as CallbackOutcomeKey))
    .map((k) => {
      const key = k as CallbackOutcomeKey;
      const raw = OUTCOME_OPTIONS.find((o) => o.key === key)?.label || 'Cancelled';
      const label = key === 'icf_sent' ? raw : raw.toLowerCase();
      return { label, tone: OUTCOME_TONE[key], n: counts.get(key) || 0 };
    });
}

export const toneDot = (t: Tone) => DOT[t];
